import { Avatar, Box, Chip, Card as MuiCard, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { gray } from '@theme/primitives';

/**
 * A row in the inspection list. The card the accordion used to be, without the fold: a row
 * opens the inspection's own page now, so the whole card is one click target.
 */
/**
 * A row in the inspection list, sized like a collapsed checklist row.
 *
 * `padding: 4` overrides the 16 the theme gives every Card: the row's own contents carry
 * their spacing, and the two paddings stacked put this list 12px wider inside and 24px
 * taller per row than the checklist beside it. Four is what the theme gives an Accordion,
 * which is what the checklist rows are.
 */
export const InspectionRowCard = styled(MuiCard)(({ theme }) => ({
  marginBottom: 8,
  padding: 4,
  overflow: 'hidden',
  cursor: 'pointer',
  // The surface the theme gives an Accordion, which is what a checklist row is: the Card
  // override paints gray[800] with no shadow, so the same border read differently against
  // it. The hover belongs to the row's inner box — this background carries `!important`,
  // and a hover rule without it would never win.
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
}));

export const StatusAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== 'statusColor',
})<{ statusColor: string }>(({ theme, statusColor }) => ({
  backgroundColor: statusColor || '#757575',
  color: theme.palette.common.white,
  width: 40,
  height: 40,
  fontSize: '16px',
  marginRight: '6px'
}));

export const PoiInfo = styled(Box)({
  flexGrow: 1,
});

export const PoiTitle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const PoiDescription = styled(Typography)(({ theme }) => ({
  marginTop: 4,
  color: theme.palette.text.secondary,
}));

export const ResourceAvatar = styled(Avatar)<{ index: number }>(({ theme, index }) => ({
  width: 32,
  height: 32,
  backgroundColor: theme.palette.grey[200],
  border: 2,
  borderColor: theme.palette.background.paper,
  marginLeft: index > 0 ? -4 : 0,
}));

export const MoreResourcesAvatar = styled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  backgroundColor: theme.palette.grey[100],
  border: 2,
  borderColor: theme.palette.background.paper,
  marginLeft: -4,
  fontSize: 12,
}));

/** Leading tile on the inspection's own page, the same one the other detail headers carry. */
export const InspectionIconTile = styled(Box)({
  width: 52,
  height: 52,
  flexShrink: 0,
  borderRadius: '.6rem',
  backgroundColor: '#26272F',
  border: '1px solid rgb(54, 56, 67)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#B5B7C8',
});

export const EmptyPOIContainer = styled(Box)({
  padding: 32,
  textAlign: 'center',
});

// Spacing between the header's chips comes from the row's own gap, so they stay evenly
// spaced however many of them are shown.
export const InfoChip = styled(Chip)({
  height: 24,
  fontSize: '0.75rem',
  paddingLeft: '8px !important',
  paddingRight: '6px !important'
});

/**
 * The clickable inside of a row: the sides an AccordionSummary pads, and its hover.
 *
 * `gray[800]` rather than `action.hover` because that is the colour the theme gives
 * `MuiAccordionSummary`, and a checklist row is one — the generic hover read lighter.
 */
export const InspectionRowBody = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '0 16px',
  borderRadius: 8,
  transition: 'background-color 120ms ease',
  '&:hover': { backgroundColor: gray[800] },
});

/** Same definition as `ChecklistHeader`: the two lists read as one. */
export const InspectionHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

export const InspectionInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

export const SnapshotChip = styled(Chip)(({ theme }) => ({
  height: 24,
  fontSize: '0.75rem',
  fontWeight: 600,
  paddingLeft: '6px !important',
  paddingRight: '6px !important',
  '& .MuiChip-icon': {
    marginLeft: 2,
    marginRight: -2,
    color: theme.palette.primary.main,
  },
}));